import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NodeProps } from 'reactflow';
import { useDispatch } from 'react-redux';
import { Trigger, useWorkflowsTriggersQuery } from 'src/services/workflows';
import { getIconForTriggerCategory, getColorForTriggerCategory } from 'src/utils/general';
import { setSelectedTrigger, clearSelectedTrigger, setNodeConfiguration } from 'src/redux/workflows/slice';
import Badge from 'src/components/ui/Badge/Badge';
import WorkflowNodeCard from '../WorkflowNodeCard/WorkflowNodeCard';
import cardStyles from '../WorkflowNodeCard/WorkflowNodeCard.module.scss';
import TriggerSelectionOverlay from './TriggerSelectionOverlay/TriggerSelectionOverlay';

const TriggerNode: React.FC<NodeProps> = ({ id, data }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedTriggerLocal, setSelectedTriggerLocal] = useState<Trigger | null>(null);
  const [selectedTriggerGroup, setSelectedTriggerGroup] = useState<string>('');
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  const { data: triggerGroupsData } = useWorkflowsTriggersQuery();

  // Restore from existing data when viewing an existing workflow
  useEffect(() => {
    if (data.triggerData && !selectedTriggerLocal && triggerGroupsData) {
      const { id: triggerId, name } = data.triggerData;

      // Find the matching trigger and group from the API data
      let matchedTrigger: Trigger | null = null;
      let matchedGroupLabel = '';

      for (const group of triggerGroupsData) {
        const found = group.items?.find((tr: Trigger) => tr.id === triggerId);
        if (found) {
          matchedTrigger = found;
          matchedGroupLabel = group.label || '';
          break;
        }
      }

      setSelectedTriggerLocal(
        matchedTrigger || {
          id: triggerId,
          name: name || 'Trigger',
          description: null,
          category: null,
          event_type: null,
        },
      );
      setSelectedTriggerGroup(matchedGroupLabel);
    }
  }, [data.triggerData, triggerGroupsData]);

  const handleAddNode = () => {
    if (data.onAddNode) {
      data.onAddNode(id, 'plus');
    }
  };

  const handleTriggerSelect = (trigger: Trigger, groupLabel: string, model: string) => {
    if (!data.isEditMode) return;

    setSelectedTriggerLocal(trigger);
    setSelectedTriggerGroup(groupLabel);

    if (trigger.id && trigger.name) {
      dispatch(
        setSelectedTrigger({
          id: trigger.id,
          name: trigger.name,
          model,
        }),
      );

      dispatch(
        setNodeConfiguration({
          nodeId: id,
          nodeType: 'trigger',
          data: {
            triggerId: trigger.id,
            name: trigger.name,
          },
        }),
      );
    }

    handleAddNode();
  };

  const handleResetClick = () => {
    if (!data.isEditMode) return;

    setSelectedTriggerLocal(null);
    setSelectedTriggerGroup('');
    dispatch(clearSelectedTrigger());

    if (data.removeLastNode) {
      data.removeLastNode();
    }
  };

  return (
    <WorkflowNodeCard
      id={id}
      label={t('workflows.node_label_trigger')}
      title={t('workflows.trigger_overlay.start_when')}
      cardText={t('workflows.trigger_overlay.choose_start')}
      isEditMode={data.isEditMode}
      isDeletable={false}
      isSelected={!!selectedTriggerLocal}
      selectedTitle={selectedTriggerLocal?.name}
      selectedSubtitle={t('workflows.trigger')}
      selectedIcon={getIconForTriggerCategory(selectedTriggerGroup)}
      summary={
        selectedTriggerLocal ? (
          <>
            {selectedTriggerGroup && (
              <div className={cardStyles.chips}>
                <Badge
                  expanded
                  label={selectedTriggerGroup}
                  colorOverride={getColorForTriggerCategory(selectedTriggerLocal.category || '')}
                />
              </div>
            )}
            {selectedTriggerLocal.description && (
              <span className="typo-text-12 text-faded">{selectedTriggerLocal.description}</span>
            )}
            <span className="typo-text-12 text-faded">
              {t('workflows.applies_to_new_events')}
              {typeof data.oneTime === 'boolean' &&
                ` · ${t('workflows.trigger_mode')}: ${t(
                  data.oneTime ? 'workflows.one_time_label' : 'workflows.every_event_label',
                )}`}
            </span>
          </>
        ) : undefined
      }
      onCardClick={() => setIsOverlayOpen(true)}
      onResetClick={handleResetClick}>
      <TriggerSelectionOverlay
        isOpen={isOverlayOpen}
        onClose={() => setIsOverlayOpen(false)}
        onTriggerSelect={handleTriggerSelect}
      />
    </WorkflowNodeCard>
  );
};

export default TriggerNode;
